import type { Accomplishment } from '../../stores/libraryStore'

interface AccomplishmentDetailProps {
  accomplishment: Accomplishment
  onClose: () => void
  onEdit: () => void
  onFramings: () => void
}

export default function AccomplishmentDetail({
  accomplishment,
  onClose,
  onEdit,
  onFramings,
}: AccomplishmentDetailProps) {
  const formatTag = (term: string) =>
    term.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 pt-16 px-4">
      <div className="w-full max-w-2xl rounded-lg bg-white shadow-xl max-h-[80vh] overflow-y-auto">
        <div className="border-b border-stone-100 px-6 py-4 flex items-center justify-between">
          <h2 className="font-semibold text-stone-800 text-sm">Proof Details</h2>
          <button
            onClick={onClose}
            className="text-stone-400 hover:text-stone-600 text-lg"
          >
            &times;
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="text-xs font-medium text-stone-500">Employer</label>
            <p className="text-sm text-stone-800">{accomplishment.employer}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-stone-500">Role</label>
              <p className="text-sm text-stone-800">{accomplishment.role_title}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-stone-500">Period</label>
              <p className="text-sm text-stone-800">{accomplishment.period ?? '—'}</p>
            </div>
          </div>

          {accomplishment.authority_notes && (
            <div>
              <label className="text-xs font-medium text-stone-500">Authority Notes</label>
              <p className="text-sm text-stone-700 whitespace-pre-wrap">
                {accomplishment.authority_notes}
              </p>
            </div>
          )}

          {accomplishment.role_context && (
            <div>
              <label className="text-xs font-medium text-stone-500">Role Context</label>
              <p className="text-sm text-stone-700 whitespace-pre-wrap">
                {accomplishment.role_context}
              </p>
            </div>
          )}

          {accomplishment.competency_tags.length > 0 && (
            <div>
              <label className="text-xs font-medium text-stone-500">Capabilities</label>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {accomplishment.competency_tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-stone-100 px-2.5 py-0.5 text-xs text-stone-600"
                  >
                    {formatTag(tag)}
                  </span>
                ))}
              </div>
            </div>
          )}

          {accomplishment.context_tags.length > 0 && (
            <div>
              <label className="text-xs font-medium text-stone-500">Context</label>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {accomplishment.context_tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-blue-50 px-2.5 py-0.5 text-xs text-blue-600"
                  >
                    {formatTag(tag)}
                  </span>
                ))}
              </div>
            </div>
          )}

          {accomplishment.framings && accomplishment.framings.length > 0 && (
            <div>
              <label className="text-xs font-medium text-stone-500">Perspectives</label>
              <div className="mt-1 space-y-2">
                {accomplishment.framings.map((f) => (
                  <div key={f.id} className="rounded border border-stone-100 p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium text-stone-600">
                        {f.label.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                      </span>
                      {f.approved && (
                        <span className="rounded-full bg-green-50 px-1.5 py-0.5 text-[10px] text-green-600">
                          Approved
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-stone-700">{f.text}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="text-xs text-stone-400 pt-2">
            Source: {accomplishment.source} &middot; Status: {accomplishment.extraction_status}
          </div>
        </div>

        <div className="border-t border-stone-100 px-6 py-3 flex justify-end gap-2">
          <button
            onClick={onFramings}
            className="rounded px-3 py-1.5 text-xs text-stone-600 hover:bg-stone-50 border border-stone-200"
          >
            Perspectives
          </button>
          <button
            onClick={onEdit}
            className="rounded px-3 py-1.5 text-xs text-white bg-stone-800 hover:bg-stone-700"
          >
            Edit
          </button>
        </div>
      </div>
    </div>
  )
}
